import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import Navigation from "@/components/Navigation";
import Hero from "@/components/Hero";
import FeaturedProgramme from "@/components/FeaturedProgramme";
import Services from "@/components/Services";
import AwsaiSection from "@/components/AwsaiSection";
import SmartWork from "@/components/SmartWork";
import ResearchTraining from "@/components/ResearchTraining";
import BooksResources from "@/components/BooksResources";
import WhyChoose from "@/components/WhyChoose";
import About from "@/components/About";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import SeoHead from "@/components/SeoHead";

import { ContentProvider } from "@/contexts/ContentContext";
import { AdminAuthProvider, useAdminAuth } from "@/contexts/AdminAuthContext";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import { Loader2 } from "lucide-react";

const LandingPage = () => (
    <div data-testid="landing-page" className="bg-white">
        <SeoHead />
        <Navigation />
        <main>
            <Hero />
            <FeaturedProgramme />
            <Services />
            <AwsaiSection />
            <SmartWork />
            <ResearchTraining />
            <BooksResources />
            <WhyChoose />
            <About />
            <Contact />
        </main>
        <Footer />
    </div>
);

const PublicSite = () => (
    <ContentProvider mode="published">
        <LandingPage />
    </ContentProvider>
);

// Renders the public site using DRAFT content. Requires admin auth.
const RequireAdmin = ({ children, redirectTo = "/admin" }) => {
    const { user, loading } = useAdminAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
        );
    }
    if (!user) return <Navigate to={redirectTo} replace />;
    return children;
};

const AdminPreview = () => (
    <RequireAdmin>
        <ContentProvider mode="draft">
            <div data-testid="admin-preview-banner" className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-amber-950 text-center text-[12.5px] font-medium py-1.5 tracking-wide">
                Draft preview — changes are not yet live on aureadigitalsolutions.com
            </div>
            <div className="pt-7">
                <LandingPage />
            </div>
        </ContentProvider>
    </RequireAdmin>
);

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AdminAuthProvider>
                    <Routes>
                        <Route path="/" element={<PublicSite />} />
                        <Route path="/admin" element={<AdminLogin />} />
                        <Route
                            path="/admin/dashboard"
                            element={
                                <RequireAdmin>
                                    <AdminDashboard />
                                </RequireAdmin>
                            }
                        />
                        <Route path="/admin/preview" element={<AdminPreview />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </AdminAuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
